import { useState } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Check, Copy, ExternalLink } from "lucide-react"
import { toast } from "sonner"
import { useTranslation } from "react-i18next"

interface ShareSuccessDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  shareId: string
  imageUrl: string
}

export function ShareSuccessDialog({
  open,
  onOpenChange,
  shareId,
  imageUrl,
}: ShareSuccessDialogProps) {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  // 공유 URL 생성 (VITE_BASE_PATH의 trailing slash 제거)
  const basePath = (import.meta.env.VITE_BASE_PATH || '').replace(/\/$/, '')
  const shareUrl = `${window.location.origin}${basePath}/share/${shareId}`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl)
      setCopied(true)
      toast.success(t('share.urlCopied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('share.copyFailed'))
    }
  }

  const handleOpenInNewTab = () => {
    window.open(shareUrl, '_blank')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-green-600">
            <Check className="w-5 h-5" />
            {t('share.successTitle')}
          </DialogTitle>
          <DialogDescription>
            {t('share.successDescription')}
            <br />
            {t('share.copyUrlHint')}
          </DialogDescription>
        </DialogHeader>

        {/* 이미지 미리보기 */}
        <div className="flex justify-center py-4">
          <img
            src={imageUrl}
            alt={t('share.sharedImageAlt')}
            className="max-w-full max-h-32 rounded-lg border border-slate-200"
          />
        </div>

        {/* URL 복사 영역 */}
        <div className="flex items-center gap-2">
          <Input
            value={shareUrl}
            readOnly
            className="flex-1 text-sm bg-slate-50"
          />
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleCopy}
            aria-label={t('share.copyUrl')}
            className="shrink-0"
          >
            {copied ? (
              <Check className="w-4 h-4 text-green-600" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleOpenInNewTab}
            aria-label={t('share.openInNewTab')}
            className="shrink-0"
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)}>
            {t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
